import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, Camera, Filter, X, AlertTriangle, CheckCircle, XCircle, Link2, Monitor,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

const formatSize = (gb) => {
  if (gb == null) return '-';
  const n = Number(gb);
  if (n >= 1024) return `${(n / 1024).toFixed(1)} TB`;
  return `${n.toFixed(1)} GB`;
};

const HypervCheckpointsReport = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'created_at', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      const res = await api.get(`/reports/virt/hyperv/checkpoints?${params}`);
      setItems(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar checkpoints');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => { try { setStats((await api.get('/reports/virt/hyperv/checkpoints/stats')).data); } catch (e) { console.error(e); } };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  const handleSort = (field) => setSortConfig(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ field }) => sortConfig.field !== field
    ? <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />
    : (sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />);
  const SortableHeader = ({ field, children, className = '' }) => (
    <th className={`p-4 cursor-pointer hover:bg-muted/70 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{children}<SortIcon field={field} /></div>
    </th>
  );

  const handleStatFilter = (t) => {
    setActiveStatFilter(t);
    setPagination(prev => ({ ...prev, page: 1 }));
    const map = { critical: 'critical', warning: 'warning', ok: 'ok' };
    setFilters({ search: '', status: map[t] || '' });
  };

  const getAgeBadge = (status, days) => {
    if (status === 'critical') return <Badge className="bg-red-500 text-white">{days}d</Badge>;
    if (status === 'warning') return <Badge className="bg-yellow-500 text-black">{days}d</Badge>;
    return <Badge className="bg-green-500 text-white">{days}d</Badge>;
  };
  const getAgeIcon = (status) => {
    if (status === 'critical') return <XCircle className="h-4 w-4 text-red-500" />;
    if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  const clearFilters = () => { setFilters({ search: '', status: '' }); setActiveStatFilter('all'); };
  const hasFilters = filters.search || filters.status;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Reporte Checkpoints (Hyper-V)</h1>
          <p className="text-sm text-muted-foreground">Checkpoints por antigüedad (≥7 días warning, ≥30 días crítico)</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`} onClick={() => handleStatFilter('all')} data-testid="stat-total"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total || 0}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'critical' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`} onClick={() => handleStatFilter('critical')} data-testid="stat-critical"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-500">{stats.critical || 0}</p><p className="text-xs text-muted-foreground">Críticos (≥30d)</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'warning' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`} onClick={() => handleStatFilter('warning')} data-testid="stat-warning"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-yellow-500">{stats.warning || 0}</p><p className="text-xs text-muted-foreground">Warning (7-30d)</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'ok' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`} onClick={() => handleStatFilter('ok')} data-testid="stat-ok"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-500">{stats.ok || 0}</p><p className="text-xs text-muted-foreground">OK (&lt;7d)</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{formatSize(stats.total_size_gb)}</p><p className="text-xs text-muted-foreground">Tamaño Total</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.vms_with_checkpoints || 0}</p><p className="text-xs text-muted-foreground">VMs afectadas</p></CardContent></Card>
          </div>
        )}

        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Filtros:</span></div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar checkpoint o VM..." value={filters.search}
                  onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setActiveStatFilter(''); }}
                  className="h-9" data-testid="checkpoint-search-input" />
              </div>
              <Select value={filters.status || 'todos'} onValueChange={(v) => { setFilters(prev => ({ ...prev, status: v === 'todos' ? '' : v })); setActiveStatFilter(''); }}>
                <SelectTrigger className="w-40 h-9" data-testid="checkpoint-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="critical">Críticos (≥30d)</SelectItem>
                  <SelectItem value="warning">Warning (7-30d)</SelectItem>
                  <SelectItem value="ok">OK (&lt;7d)</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" /> Limpiar</Button>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="checkpoints-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">CHECKPOINT</SortableHeader>
                  <SortableHeader field="vm_name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="checkpoint_type" className="text-left">TIPO</SortableHeader>
                  <SortableHeader field="created_at" className="text-left">CREADO</SortableHeader>
                  <SortableHeader field="age_days" className="text-center justify-center">ANTIGÜEDAD</SortableHeader>
                  <SortableHeader field="size_gb" className="text-center justify-center">TAMAÑO</SortableHeader>
                  <th className="text-left p-4">SERVIDOR</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">No hay checkpoints con este estado</td></tr>
                ) : items.map(c => (
                  <tr key={c.id} className="border-b hover:bg-muted/30" data-testid={`checkpoint-row-${c.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2"><Camera className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{c.name}</span>{c.is_current === 1 && <Badge variant="secondary" className="text-xs">Actual</Badge>}</div>
                    </td>
                    <td className="p-4"><div className="flex items-center gap-1"><Monitor className="h-3 w-3 text-muted-foreground" />{c.vm_name}</div></td>
                    <td className="p-4"><Badge variant="outline">{c.checkpoint_type || '-'}</Badge></td>
                    <td className="p-4 text-sm text-muted-foreground">{c.created_at ? formatDateARG(c.created_at) : '-'}</td>
                    <td className="p-4 text-center"><div className="flex items-center justify-center gap-2">{getAgeIcon(c.alert_status)}{getAgeBadge(c.alert_status, c.age_days)}</div></td>
                    <td className="p-4 text-center">{formatSize(c.size_gb)}</td>
                    <td className="p-4">
                      {c.server_id ? (
                        <Link to={`/assets/servers?search=${encodeURIComponent(c.server_hostname)}`} className="flex items-center gap-1 text-blue-500 hover:underline text-sm"><Link2 className="h-3 w-3" />{c.server_hostname}</Link>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages} ({pagination.total} checkpoints)</span>
            <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HypervCheckpointsReport;
